import { IPharmacyInfo } from "@/Common/Api/Pharmacy/Dto/Responses/IPharmacyInfo";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { AttachedObjectResponse } from "@/Common/Models/FileSelection/Responses/AttachedObject";
import { FileType } from "@/Common/Models/FileType";
import { SignedPrintAuthorization } from "@/Common/Models/SignedPrintAuthorization";
import { ExportableFileGenerationIntent } from "@/ExportableFile/Models/enum/ExportableFileGenerationIntent";
import { DraggableImageOverlayElement } from "@/ExportableFile/Models/OverlayElements/DraggableImageOverlayElement";
import { DraggableImageOverlayType, OverlayElement, OverlayElementType } from "@/ExportableFile/Models/OverlayElements/OverlayElement";
import { SignPrintAuthorizationOverlayElement } from "@/ExportableFile/Models/OverlayElements/SignPrintAuthorizationOverlayElement";
import { ExportMode } from "./enum/ExportMode";
import { FileFormat } from "./enum/FileFormat";
import { FileTypeTemporaryFile } from "./FileTypes/FileTypeTemporaryFile";
import { PrepositionedPharmacyInfoOverlayElement } from "./OverlayElements/PrepositionedPharmacyInfoOverlayElement";
import { ExportableFileDisplayOption, ExportableFileModel } from "./ExportableFileTableItem";
import { GenerateFileOptions } from "./IGenerateFileOptions";

interface IExportableFile {
    description: string;
    date: string;
}

interface IExportableFileLoaded {
    OriginalExportableFile: ExportableFile;
    PregeneratedTemporaryFile?: FileTypeTemporaryFile;
    FetchUrl: string;
}

interface IExportableDto {
    ObjectId: string;
    FileType: FileType;
    Description: string;
    OriginalFileType?: FileType;
    OriginalObjectId?: string;
    PageNumbers?: number[];
}

abstract class ExportableFile implements IExportableFile {
    public description: string;
    public date: string;

    public fileType: FileType;
    public fileFormat: FileFormat;
    public totalPages: number;
    public sizeBytes: number;
    public showOverlayRefreshButton: boolean = false;

    public PageNumbers: number[];

    public isExternal: boolean;
    public clientTag: string;
    public claimId: string;

    public canGenerateFileXcom: boolean = false;

    public isDefaultPositionSignatureRequired: boolean = false;

    protected _overlayElements: OverlayElement[] = [];
    private _allowedDraggableImageType: DraggableImageOverlayType[] = [DraggableImageOverlayType.Signature];

    public get draggableImageOverlayElement(): DraggableImageOverlayElement[] {
        return this._overlayElements
            .filter(p => p.overlayElementType === OverlayElementType.DraggableImage)
            .map(p => new DraggableImageOverlayElement(p));
    }

    public get allowedDraggableImageType(): DraggableImageOverlayType[] {
        return this._allowedDraggableImageType;
    }

    protected set allowedDraggableImageType(value: DraggableImageOverlayType[]) {
        this._allowedDraggableImageType = value;
    }

    public get overlayElements(): OverlayElement[] {
        return this._overlayElements;
    }

    public applyOverlayElement(overlay: OverlayElement): ExportableFile {
        this._overlayElements.push(overlay);
        this.cachedGeneratedTemporaryFileWithOverlays = null;
        return this;
    }

    public clearGeneratedFileWithOverlayCache(): void {
        this.cachedGeneratedTemporaryFileWithOverlays = null;
    }

    public applyDistinctOverlayElements(value: OverlayElement[], distinctType: OverlayElementType): ExportableFile {
        this._overlayElements = [
            ...this._overlayElements.filter(p => p.overlayElementType !== distinctType),
            ...value
        ];
        this.cachedGeneratedTemporaryFileWithOverlays = null;
        return this;
    }

    public applyPrepositionedPharmacyInfoOverlay(pharmacy: IPharmacyInfo, isSetPreferred: boolean = false, hidePharmacists: boolean = false): ExportableFile {
        this.applyDistinctOverlayElements(
            [new PrepositionedPharmacyInfoOverlayElement({ Pharmacy: { ...pharmacy }, IsSetPreferred: isSetPreferred }, hidePharmacists)],
            OverlayElementType.PrepositionedPharmacyInfo
        );
        return this;
    }

    public removeOverlayElements(): void {
        this._overlayElements = [];
    }

    private _internalId: string;
    public get InternalId(): string {
        if (!this._internalId)
            this._internalId = this.buildExportableFileInternalId(this.fileType, this.getAttachedObject_ObjectId());
        return this._internalId;
    }

    protected cachedGeneratedTemporaryFileWithoutOverlays: FileTypeTemporaryFile;
    protected cachedGeneratedTemporaryFileWithOverlays: FileTypeTemporaryFile;

    constructor(fileType: FileType) {
        this.fileType = fileType;
    }

    private buildExportableFileInternalId(fileType: FileType, attachedObjectId: string): string {
        return `${fileType}_${attachedObjectId}`;
    }

    protected abstract generateFileIfNeeded(generateFileOption: GenerateFileOptions): Promise<IExportableFileLoaded>;

    public saveOverlayPositionsIfNeeded(): Promise<void> {
        return Promise.resolve();
    }

    // "optional abstract" methods
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected generateFileIfNeededXcom(includeOverlays: boolean): Promise<IExportableFileLoaded> {
        throw "Not Supported";
    }

    public abstract getMeta(): Promise<number>;
    public abstract getAttachedObject_ObjectId(): string; // The ObjectId that would be used if this file was used in AttachedObject Table
    public abstract getCleanCopy(): ExportableFile;
    public clone(removeSignature: boolean = false): ExportableFile {
        const cleanCopy = this.getCleanCopy();
        cleanCopy._overlayElements = [...this._overlayElements];

        if (removeSignature) {
            cleanCopy._overlayElements = cleanCopy._overlayElements
                .filter(p => p.overlayElementType !== OverlayElementType.PrepositionedSignature && p.overlayElementType !== OverlayElementType.SignedPrintAuthorization)
                .filter(p => p.overlayElementType !== OverlayElementType.DraggableImage || (p as DraggableImageOverlayElement)?.draggableImageOverlayType !== DraggableImageOverlayType.Signature);
        }

        return cleanCopy;
    }

    // "optional abstract" methods
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public async persistSignature(encounterId?: string): Promise<void> {
        throw "Not Supported";
    }

    protected setCache(tempFile: FileTypeTemporaryFile, overlayElements: OverlayElement[]): void {
        let hasOverlays = overlayElements?.length;
        if (hasOverlays) {
            this.showOverlayRefreshButton = false;
            this.cachedGeneratedTemporaryFileWithOverlays = tempFile;
        }
        else
            this.cachedGeneratedTemporaryFileWithoutOverlays = tempFile;
    }

    private getTemporaryFileFromCache(includeOverlays: boolean = true): FileTypeTemporaryFile {
        let hasOverlays = includeOverlays && (this._overlayElements?.length);

        return hasOverlays
            ? this.cachedGeneratedTemporaryFileWithOverlays
            : this.cachedGeneratedTemporaryFileWithoutOverlays;
    }

    public hasSignature(): boolean {
        const enableQuickDocumentSigning = cacheStore.ClinicSettings.EnableQuickDocumentSigning;

        return (
            this.draggableImageOverlayElement.some(p => p.draggableImageOverlayType === DraggableImageOverlayType.Signature)
            || this.overlayElements.some(p =>
                p.overlayElementType === OverlayElementType.PrepositionedSignature
                || p.overlayElementType === OverlayElementType.SignedPrintAuthorization
                || (enableQuickDocumentSigning && p.overlayElementType === OverlayElementType.DraggableImage)
            )
        );
    }

    public isEquivalent(file: ExportableFile): boolean {
        return this.InternalId == file.InternalId;
    }

    public isEquivalentAttachedObject(attachedObject: AttachedObjectResponse): boolean {
        return this.buildExportableFileInternalId(attachedObject.Type, attachedObject.ObjectId) == this.InternalId;
    }

    public async getFetchURL(callback: (fetchUrl: string) => void, includeOverlays: boolean = true): Promise<void> {
        const loadedFile = await this.getFetchUrlDtoAsync(includeOverlays);
        callback(loadedFile.FetchUrl);
    }

    public async getFetchUrlDtoAsync(
        includeOverlays: boolean = true,
        signPdfFormFromUserPreferences: boolean = false,
        includeOverlaysFromPreferences: boolean = false,
        deleteSignature: boolean = false,
        isFinalFile: boolean = false,
        exportMode: ExportMode = null,
        generationIntent: ExportableFileGenerationIntent = ExportableFileGenerationIntent.Preview
    ): Promise<IExportableFileLoaded> {
        // from cache
        const cachedTemporaryFile = this.getTemporaryFileFromCache(includeOverlays);
        if (cachedTemporaryFile
            && cachedTemporaryFile._overlayElements.length == this._overlayElements.length
            && this.hasSameOverlays(cachedTemporaryFile._overlayElements, this._overlayElements)
            && !deleteSignature
            && !isFinalFile
        )
            return { OriginalExportableFile: this, PregeneratedTemporaryFile: cachedTemporaryFile, FetchUrl: cachedTemporaryFile.FetchUrl };

        // generate
        if (this.isExternal) {
            return await this.generateFileIfNeededXcom(includeOverlays);
        }

        return await this.generateFileIfNeeded({
            includeOverlays: includeOverlays,
            isExternal: false,
            signPdfFormFromUserPreferences: signPdfFormFromUserPreferences,
            includeOverlaysFromPreferences: includeOverlaysFromPreferences,
            isFinalFile: isFinalFile,
            exportMode: exportMode,
            exportableFileGenerationIntent: generationIntent
        });
    }

    public hasSameOverlays(overlayElements: OverlayElement[], overlayElements2: OverlayElement[]): boolean {
        const overlayElementsDictionary = overlayElements.sortByProperty(p => p.overlayElementType).sortByProperty(p => p.id);
        const overlayElements2Dictionary = overlayElements2.sortByProperty(p => p.overlayElementType).sortByProperty(p => p.id);
        return JSON.stringify(overlayElementsDictionary) === JSON.stringify(overlayElements2Dictionary);
    }

    public openInNewWindow(): void {
        this.getFetchURL(url => {
            MyleHelper.OpenNewWindow(url);
        });
    }

    public async print(
        includeOverlays: boolean = true,
        isFinalFile: boolean = false,
        exportMode: ExportMode = null,
        generationIntent: ExportableFileGenerationIntent = ExportableFileGenerationIntent.Preview
    ): Promise<void> {
        const exportableFileLoaded = await this.getFetchUrlDtoAsync(includeOverlays, false, false, false, isFinalFile, exportMode, generationIntent);
        PrintHelper.ShowPrintPreview(apiSettings.buildAppUrl(exportableFileLoaded.FetchUrl));
    }

    public toExportableFileModel(displayOption?: ExportableFileDisplayOption): ExportableFileModel {
        return new ExportableFileModel(this, displayOption);
    }

    public async toFileDto(includeOverlays: boolean = true, isFinalFile: boolean = false, exportMode: ExportMode = null, generationIntent: ExportableFileGenerationIntent = ExportableFileGenerationIntent.Preview): Promise<IExportableDto> {
        // Check if a temporary file with Overlays needs to be generated or is already available
        let exportableFileLoaded = await this.getFetchUrlDtoAsync(includeOverlays, false, false, false, isFinalFile, exportMode, generationIntent);
        if (exportableFileLoaded && exportableFileLoaded.PregeneratedTemporaryFile) {
            const value = await exportableFileLoaded.PregeneratedTemporaryFile.toFileDto();
            value.OriginalFileType = this.fileType;
            value.OriginalObjectId = this.getAttachedObject_ObjectId();
            value.PageNumbers = this.PageNumbers;
            return value;
        }

        // There is no new temporary File related to the ExportableFile, return the original ExportableFile reference directly
        let fileDto: IExportableDto = {
            ObjectId: this.getAttachedObject_ObjectId(),
            FileType: this.fileType,
            Description: this.description,
            PageNumbers: this.PageNumbers
        };

        return fileDto;
    }

    public toAttachedObject(): AttachedObjectResponse {
        return new AttachedObjectResponse({
            ObjectId: this.getAttachedObject_ObjectId(),
            Type: this.fileType,
            Description: this.description,
            AssociatedSignedPrintAuthorization: this.getSignPrintAuthorization()
        });
    }

    public serializeOverlayElements(includeOverlays: boolean = true): string[] {
        return (includeOverlays && this.overlayElements.map(p => JSON.stringify(p))) || [];
    }

    public getSignPrintAuthorization(): SignedPrintAuthorization | null {
        const overlay = this._overlayElements
            .firstOrDefault(p => p.overlayElementType === OverlayElementType.SignedPrintAuthorization) as SignPrintAuthorizationOverlayElement;

        return overlay?.SignedPrintAuthorization;
    }
}

export { ExportableFile, IExportableDto, IExportableFile, IExportableFileLoaded };
